import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertVariant } from '@patternfly/react-core';
import { AlertGroupProvider, useFeature } from '@ole-ui/ole-ui-components';
import axios from 'axios';
import { observer } from 'mobx-react';
import useMobxStores from 'hooks/useMobxStores';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { USER_LAB_API } from 'config/constants';
import {
  getLabPassedNotification,
  setLabPassedNotificationSeen,
} from 'services/LabGradeResultsService';
import { LabPassedNotification, LabsStatus, UserLab } from 'types/labs';

const { get } = axios;

const IDLE_POLLING_MS = 10 * 60 * 1000; // 10 minutes
const COURSE_PAGE_POLLING_MS = 60 * 1000; // 1 minute

const LabPassedAlerts: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { routerStore } = useMobxStores();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();

  const isOnCoursePage = routerStore.route?.name === 'courses:page';

  const isLabNotificationEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.LabNotificationEnabled,
    section: 'enablements',
  });

  const shownNotificationsRef = useRef<Set<string>>(new Set());
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const currentIntervalRef = useRef<number>(IDLE_POLLING_MS);

  const fetchRunningLabs = useCallback(async (): Promise<boolean> => {
    try {
      const result = await get(USER_LAB_API);
      const labs: UserLab[] = result.data?.items ?? [];
      return labs.some((lab) => lab.state === LabsStatus.Running);
    } catch {
      return false;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const showNotificationAlert = useCallback(
    (notification: LabPassedNotification) => {
      const alertKey = `lab-passed-${notification.uuid}`;

      if (shownNotificationsRef.current.has(notification.uuid)) {
        return;
      }

      shownNotificationsRef.current.add(notification.uuid);

      const courseSlug = notification.course_slug.toUpperCase();
      const labTitle = notification.lab_title.replace('Lab: ', '');

      addAlert({
        children: t('You passed "{{labTitle}}" ({{courseSlug}})', {
          courseSlug,
          labTitle,
        }),
        key: alertKey,
        title: t('Lab passed!'),
        variant: AlertVariant.success,
        removeAction: () => setLabPassedNotificationSeen(notification.uuid),
        timeout: 30 * 1000,
        onTimeout: () => setLabPassedNotificationSeen(notification.uuid),
      });
    },
    [addAlert, t],
  );

  const setPollingInterval = useCallback(
    (intervalMs: number, fetchFn: () => Promise<void>) => {
      if (
        currentIntervalRef.current === intervalMs &&
        pollingIntervalRef.current
      ) {
        return;
      }

      stopPolling();
      currentIntervalRef.current = intervalMs;
      pollingIntervalRef.current = setInterval(fetchFn, intervalMs);
    },
    [stopPolling],
  );

  const fetchNotifications = useCallback(async () => {
    if (document.hidden) return;

    const hasRunningLab = isOnCoursePage ? await fetchRunningLabs() : false;
    const interval =
      isOnCoursePage && hasRunningLab
        ? COURSE_PAGE_POLLING_MS
        : IDLE_POLLING_MS;

    try {
      const response = await getLabPassedNotification();
      const notifications = response?.items ?? [];

      notifications.forEach((notification) => {
        showNotificationAlert(notification);
      });

      setPollingInterval(interval, fetchNotifications);
    } catch (error) {
      if (error?.response?.status === 403) {
        stopPolling();
      }
    }
  }, [
    fetchRunningLabs,
    isOnCoursePage,
    setPollingInterval,
    showNotificationAlert,
    stopPolling,
  ]);

  useEffect(() => {
    if (!isLabNotificationEnabled) return undefined;

    const startPolling = async () => {
      const hasRunningLab = isOnCoursePage ? await fetchRunningLabs() : false;
      const initialInterval =
        isOnCoursePage && hasRunningLab
          ? COURSE_PAGE_POLLING_MS
          : IDLE_POLLING_MS;

      fetchNotifications();

      pollingIntervalRef.current = setInterval(
        fetchNotifications,
        initialInterval,
      );
    };

    startPolling();

    return () => {
      stopPolling();
    };
  }, [
    fetchNotifications,
    fetchRunningLabs,
    isLabNotificationEnabled,
    isOnCoursePage,
    stopPolling,
  ]);

  return null;
};

export default observer(LabPassedAlerts);
